import React, { useState, useEffect, useCallback, useRef } from 'react';
import { buildUnderlagPath } from '../../../sharepoint/domain/pathBuilders';
import { API_URL } from '../../../../utils/api';
import SkissModal from './SkissModal';

// Backend sätter prefixet när en kund laddar upp till BK Underlag
// (Underlag_sharepointRoutes.js, KUND_PREFIX). Det är så kundens filer känns igen.
const KUND_PREFIX = 'KUND - ';
const KUND_ACCEPT = '.pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.ppt,.pptx';

/**
 * SkissSection - Visar BK-skiss-PDF i estimat (om sådan finns i Underlag-mappen).
 * Hittar fil med "Skiss" i namnet och låter användaren öppna en granskningsmodal.
 *
 * Kunden kan också ladda upp eget underlag (skisser, bilder) hit. Filerna hamnar
 * i samma mapp som BK:s, med prefixet "KUND - ", och listas för sig.
 */
const SkissSection = ({ procurementData, customer: customerProp, lead: leadProp, displayName, currentUser, onHarUnderlag }) => {
  const [files, setFiles] = useState([]); // BK:s PDF:er i BK Underlag-mappen
  const [kundFiler, setKundFiler] = useState([]); // Kundens egna uppladdningar, alla filtyper
  const [mappSokvag, setMappSokvag] = useState(null);
  const [laddarUpp, setLaddarUpp] = useState(null); // { klara, totalt }
  const filInput = useRef(null);
  const [loading, setLoading] = useState(true);
  const [modalFile, setModalFile] = useState(null); // Vilken fil som är öppen i modalen
  const [commentStatsMap, setCommentStatsMap] = useState({}); // { fileId: { total, fromOthers } }

  const arKund = ['kund', 'extrakund'].includes(currentUser?.role);

  const fetchSkiss = useCallback(async (signal) => {
    if (!procurementData) {
      setLoading(false);
      return;
    }

    try {
      // Hämta namn från flera möjliga källor (BK-vy: customer/lead som props, kundvy: procurementData.leadId populerad)
      const populatedLead = procurementData.leadId && typeof procurementData.leadId === 'object' ? procurementData.leadId : null;

      // Försök separata fält först, fall sedan tillbaka på "name" som en sträng
      let firstName =
        customerProp?.firstName ||
        leadProp?.customerInfo?.firstName ||
        populatedLead?.customerInfo?.firstName ||
        '';
      let lastName =
        customerProp?.lastName ||
        leadProp?.customerInfo?.lastName ||
        populatedLead?.customerInfo?.lastName ||
        '';

      // Fallback: dela upp full name-sträng (från lead.customerInfo.name eller displayName)
      if (!firstName || !lastName) {
        const fullName = leadProp?.customerInfo?.name || populatedLead?.customerInfo?.name || displayName || '';
        const parts = fullName.trim().split(/\s+/);
        if (parts.length >= 2) {
          firstName = firstName || parts[0];
          lastName = lastName || parts.slice(1).join(' ');
        }
      }

      if (!firstName || !lastName) {
        console.warn('SkissSection: kunde inte hitta kundnamn');
        setLoading(false);
        return;
      }

      const customer = {
        firstName,
        lastName,
        projectId: procurementData.projectId,
        projectYear: procurementData.projectYear || (procurementData.createdAt ? new Date(procurementData.createdAt).getFullYear().toString() : new Date().getFullYear().toString())
      };

      const projectName = procurementData.projectId ? `#${procurementData.projectId}` : '';

      // Bygg sökväg: /Underlag/Underlag-delad/BK Underlag/
      //
      // isPUMode=true ger rot-formen "Leadme/{Namn} (#id)/..." med flit, även
      // för sålda kunder vars mapp ligger i "- Ja/{år}/". Backend slår upp
      // kundmappen där den faktiskt ligger (roten, "- Ja" eller "- Nej"), så
      // rot-formen är den robusta: alternativet vore att gissa årtalet här, och
      // en felgissning ger tom vy. Ändra inte till den årsbaserade grenen.
      const underlagDeladPath = buildUnderlagPath(customer, projectName, true);
      if (!underlagDeladPath) {
        setLoading(false);
        return;
      }
      const bkUnderlagPath = `${underlagDeladPath}/BK Underlag`;
      if (!signal?.aborted) setMappSokvag(bkUnderlagPath);

      const token = localStorage.getItem('token');
      let pdfFiles = [];

      // Bail om ingen token (t.ex. efter logout) — annars blir det 401 i konsolen
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const res = await fetch(`${API_URL}/underlag/files-silent`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ folderPath: bkUnderlagPath }),
          signal
        });
        if (res.ok) {
          const data = await res.json();
          const allaFiler = (data || []).filter(f => !f.isFolder);
          pdfFiles = allaFiler.filter(f =>
            !f.name.startsWith(KUND_PREFIX) && f.name.toLowerCase().endsWith('.pdf')
          );
          if (!signal?.aborted) {
            setFiles(pdfFiles);
            setKundFiler(allaFiler.filter(f => f.name.startsWith(KUND_PREFIX)));
          }
        }
      } catch (_) { /* tysta (inkluderar AbortError vid unmount) */ }
      if (signal?.aborted) return;

      // Hämta kommentarsstatistik för varje fil
      if (pdfFiles.length > 0 && procurementData._id) {
        const statsMap = {};
        for (const pdfFile of pdfFiles) {
          if (signal?.aborted) return;
          try {
            const reviewRes = await fetch(
              `${API_URL}/api/estimat-skiss/procurement/${procurementData._id}/file/${encodeURIComponent(pdfFile.id)}`,
              { headers: { 'Authorization': `Bearer ${token}` }, signal }
            );
            if (reviewRes.ok) {
              const review = await reviewRes.json();
              if (review) {
                const myRole = currentUser?.role || '';
                let total = 0;
                let fromOthers = 0;
                let utkast = 0;
                for (const ann of review.annotations || []) {
                  for (const c of ann.comments || []) {
                    total++;
                    // Utkast är skrivna men inte skickade. De räknas för sig —
                    // annars ser åtta anteckningar kunden aldrig skickat ut som
                    // åtta besked.
                    if (c.draft) { utkast++; continue; }
                    if (c.authorRole && c.authorRole !== myRole) fromOthers++;
                  }
                }
                for (const c of review.commentThread || []) {
                  total++;
                  if (c.authorRole && c.authorRole !== myRole) fromOthers++;
                }
                statsMap[pdfFile.id] = { total, fromOthers, utkast };
              }
            }
          } catch (_) { /* tysta (inkluderar AbortError vid unmount) */ }
        }
        if (!signal?.aborted) setCommentStatsMap(statsMap);
      }
    } catch (err) {
      console.error('Fel vid hämtning av underlag:', err);
    } finally {
      setLoading(false);
    }
  }, [procurementData, customerProp, leadProp, displayName, currentUser]);

  useEffect(() => {
    const controller = new AbortController();
    fetchSkiss(controller.signal);
    return () => controller.abort();
  }, [fetchSkiss]);

  /**
   * Rapporterar uppåt om mappen innehåller något. Komponenten renderar null när
   * den är tom, men den som ramar in den — t.ex. kundens Underlag-sektion i
   * MyProjects — behöver veta det för att kunna dölja rubriken också. Utan det
   * ser kunden en tom Underlag-ruta, vilket blir vanligt nu när upphandlingar
   * kan starta utan estimat och därmed utan skiss.
   *
   * Skicka in en stabil funktion (t.ex. en setState) — en inline-arrow gör att
   * effekten körs vid varje omritning.
   */
  useEffect(() => {
    if (loading) return;
    onHarUnderlag?.(files.length + kundFiler.length > 0);
  }, [loading, files.length, kundFiler.length, onHarUnderlag]);

  // Ladda upp filerna en i taget, tyst, och skicka sedan EN samlad notis till
  // BK. Går en fil fel ligger de andra redan i SharePoint och räknas ändå.
  const handleLaddaUpp = async (e) => {
    const valda = Array.from(e.target.files || []);
    e.target.value = '';
    if (!valda.length || !mappSokvag) return;

    const token = localStorage.getItem('token');
    const uppladdade = [];
    const misslyckade = [];
    setLaddarUpp({ klara: 0, totalt: valda.length });

    for (const file of valda) {
      try {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('folderPath', mappSokvag);
        fd.append('tyst', 'true');
        const resp = await fetch(`${API_URL}/underlag/upload`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' },
          body: fd
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) throw new Error(data.error || 'Uppladdning misslyckades');
        uppladdade.push(data.fileName || file.name);
      } catch (err) {
        console.error(`Fel vid uppladdning av ${file.name}:`, err);
        misslyckade.push(`• ${file.name}: ${err.message}`);
      }
      setLaddarUpp(prev => prev && { ...prev, klara: prev.klara + 1 });
    }

    if (uppladdade.length > 0) {
      try {
        await fetch(`${API_URL}/underlag/kunduppladdning-klar`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ folderPath: mappSokvag, fileNames: uppladdade })
        });
      } catch (_) { /* notisen är bästa-försök — filerna ligger redan uppe */ }
      await fetchSkiss();
    }

    setLaddarUpp(null);
    if (misslyckade.length) {
      alert(`Kunde inte ladda upp:\n${misslyckade.join('\n')}` +
        (uppladdade.length ? '\n\nÖvriga filer laddades upp.' : ''));
    }
  };

  // Öppnar en kundfil i ny flik. Fönstret öppnas FÖRE hämtningen, annars
  // stoppas det av popup-blockeraren när klicket väl är långt borta.
  const oppnaKundFil = async (fil) => {
    const flik = window.open('', '_blank');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/underlag/file/${fil.driveId}/${fil.id}?preview=true`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Kunde inte hämta filen');
      const url = URL.createObjectURL(await res.blob());
      if (flik) flik.location.href = url;
      else window.open(url, '_blank');
    } catch (err) {
      console.error('Fel vid öppning av fil:', err);
      flik?.close();
      alert('Kunde inte öppna filen.');
    }
  };

  if (loading) return null;
  // Kunden ser alltid sektionen när mappen går att räkna ut — annars finns
  // ingen plats att ladda upp från. Övriga ser den bara när det finns filer.
  const kanLaddaUpp = arKund && !!mappSokvag;
  if (files.length === 0 && kundFiler.length === 0 && !kanLaddaUpp) return null;

  return (
    <>
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4 shadow-sm">
        <div className="flex items-center justify-between gap-3 mb-3">
          <h3 className="text-base font-semibold text-gray-900">Underlag</h3>
          {kanLaddaUpp && (
            <>
              <input
                ref={filInput}
                type="file"
                multiple
                accept={KUND_ACCEPT}
                className="hidden"
                onChange={handleLaddaUpp}
              />
              <button
                onClick={() => filInput.current?.click()}
                disabled={!!laddarUpp}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-60 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                {laddarUpp ? `Laddar upp ${laddarUpp.klara}/${laddarUpp.totalt}…` : 'Ladda upp eget underlag'}
              </button>
            </>
          )}
        </div>
        {kanLaddaUpp && files.length === 0 && kundFiler.length === 0 && (
          <p className="text-sm text-gray-500">
            Har du egna skisser, bilder eller ritningar? Ladda upp dem här så ser din byggkonsult dem.
          </p>
        )}
        <div className="space-y-2">
          {files.map((pdfFile) => {
            const stats = commentStatsMap[pdfFile.id] || { total: 0, fromOthers: 0 };
            return (
              <div key={pdfFile.id} className="flex items-center justify-between gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <svg className="w-6 h-6 text-red-500 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                  </svg>
                  <p className="text-sm text-gray-800 truncate" title={pdfFile.name}>{pdfFile.name}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {stats.total > 0 && (
                    <button
                      onClick={() => setModalFile(pdfFile)}
                      className={`px-3 py-1.5 text-xs rounded-lg transition-colors flex items-center gap-1.5 ${
                        stats.fromOthers > 0
                          ? 'bg-orange-100 text-orange-800 hover:bg-orange-200 border border-orange-300'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
                      }`}
                      title={
                        stats.fromOthers > 0
                          ? `${stats.fromOthers} nya kommentarer`
                          : stats.utkast > 0 && stats.total === stats.utkast
                            ? `${stats.utkast} utkast — skrivna men inte skickade`
                            : `${stats.total} kommentarer`
                      }
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                      <span>
                        {stats.fromOthers > 0
                          ? `${stats.fromOthers} nya`
                          : stats.utkast > 0 && stats.total === stats.utkast
                            ? `${stats.utkast} utkast`
                            : `${stats.total} kommentar${stats.total !== 1 ? 'er' : ''}`}
                      </span>
                    </button>
                  )}
                  <button
                    onClick={() => setModalFile(pdfFile)}
                    className="px-3 py-1.5 text-xs bg-leadme-pink text-white rounded-lg hover:bg-leadme-pink-dark transition-colors"
                  >
                    Öppna
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {kundFiler.length > 0 && (
          <div className={files.length > 0 ? 'mt-4' : ''}>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
              {arKund ? 'Ditt uppladdade underlag' : 'Uppladdat av kunden'}
            </p>
            <div className="space-y-2">
              {kundFiler.map((fil) => (
                <div key={fil.id} className="flex items-center justify-between gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <svg className="w-6 h-6 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p className="text-sm text-gray-800 truncate" title={fil.name}>
                      {fil.name.slice(KUND_PREFIX.length)}
                    </p>
                  </div>
                  <button
                    onClick={() => oppnaKundFil(fil)}
                    className="px-3 py-1.5 text-xs bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex-shrink-0"
                  >
                    Öppna
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {modalFile && (
        <SkissModal
          file={modalFile}
          procurementId={procurementData._id}
          currentUser={currentUser}
          onClose={() => setModalFile(null)}
        />
      )}
    </>
  );
};

export default SkissSection;
